// @ts-check
// sync.js — "Sync" settings section (per active workspace).
//
// Extracted from settings-panel.js (Stage C of the monolith split). Owns the
// two-step email→OTP sign-in flow state. The host's _wire registers the auth
// state listener and calls handleAuthStateChange() to advance the step when
// auth completes out of band (e.g. a magic-link click in another tab).
//
// renderSync(ctx) uses ctx.row, ctx.rawRow, ctx.openExpansions, ctx.invalidate.

import { getSyncHelpers } from '../../storage/session.js';
import { openMenu, toggleMenu } from '../../ui/menu-popover.js';
import { deleteAccount } from '../../workspaces/reset-account.js';
import type { SettingsSectionCtx } from '../settings-section-api.js';
import { confirm, errorDiv, expandHeadingCta, prompt, setErrorDiv, toast } from './_common.js';

// Sync-section in-progress state (preserved across re-renders within an open
// panel). The OTP step keeps the email so "Verify" knows which account to
// confirm.
let _syncStep = 'idle'; // 'idle' | 'sent' | 'authed'
let _syncPendingEmail = '';
let _syncBusy = false;
let _syncError = '';

/**
 * @param {any} ctx - the section render context.
 * @returns {any[]} the section's rows.
 */
export function renderSync(ctx: SettingsSectionCtx) {
  const rows = [];
  const helpers = getSyncHelpers();
  const ID = 'group:sync';

  let value = 'Off';
  let available = false;
  if (helpers) {
    available = !!helpers.available;
    if (helpers.authenticated && helpers.userEmail) value = helpers.userEmail;
    else if (available && _syncStep === 'sent') value = 'Check email';
    else if (available) value = 'Sign in';
    else value = 'Off';
  } else if (window.Platform?.isWeb) {
    // runtime — Platform has no ES export
    value = 'Sign in';
  }

  const authed = !!(helpers?.authenticated && helpers.userEmail);
  // When the value is the "Sign in" CTA, render it as a clickable value: clicking
  // expands Sync and focuses the email field.
  const signInAction = value === 'Sign in';
  rows.push(
    ctx.row({
      id: ID,
      // The header value has three control variants: signed-in (the email as a
      // dropdown button → sign-out), the "Sign in" CTA button, or a plain value
      // ("Check email" / "Off"). A control is built once + retained, so the
      // variant is encoded in the key to remount cleanly when auth state changes.
      key: `group:sync:${authed ? 'authed' : signInAction ? 'action' : 'value'}`,
      label: 'Sync',
      value: authed || signInAction ? undefined : value,
      control: authed
        ? () => buildSignedInValue(ctx, helpers)
        : signInAction
          ? () => buildSignInValue(ctx, ID)
          : undefined,
      updateControl: authed
        ? (el) => {
            el.textContent = helpers.userEmail || 'Signed in';
          }
        : signInAction
          ? (el) => {
              el.textContent = 'Sign in';
            }
          : undefined,
      // No expand body when signed in — the email dropdown owns sign-out, so
      // there's nothing to fold open (and no "Signed in as" sub-row).
      expandable: !authed,
      searchExpand: !authed,
      cls: 'settings-row-group',
      searchText: `sync sign in account email sign out ${value}`,
    })
  );
  // Signed in: the email value IS the account menu (caret → Sign out); no
  // sub-content.
  if (authed) return rows;
  if (!ctx.openExpansions.has(ID)) return rows;

  if (!helpers || !available) {
    // Pre-opt-in on plain web. The helpers module loads after a click that
    // triggers the bundle download. Button self-manages its disabled state.
    rows.push(
      ctx.rawRow(() => buildEnableForm(helpers), {
        key: 'sync-form:enable',
        indent: true,
        searchText: 'sync enable account',
      })
    );
    return rows;
  }

  // Two-step email→OTP form. STRUCTURAL state (the step: idle vs sent → different
  // inputs) is in the key, so a step change cleanly remounts. ATTRIBUTE state
  // (busy → button label/disabled, error text) is patched by `updateAuthForm` on
  // the retained node — so the email/code input keeps focus + value across the
  // busy/error re-renders the submit handlers fire.
  rows.push(
    ctx.rawRow(() => buildAuthForm(ctx, helpers), {
      key: `sync-form:${_syncStep}`,
      update: updateAuthForm,
      indent: true,
      searchText: 'sync sign in email otp account',
    })
  );
  return rows;
}

// The clickable "Sign in" header value: expands the Sync group and focuses the
// email field once the form has rendered.
function buildSignInValue(ctx: SettingsSectionCtx, ID: string): HTMLElement {
  // stopPropagation:false — sign-in lets the row's own activation run too. The
  // email focus rides the next frame, after the expanded form has rendered.
  return expandHeadingCta(ctx, ID, 'Sign in', {
    title: 'Sign in to sync',
    stopPropagation: false,
    before: () => {
      requestAnimationFrame(() => {
        const el = document.querySelector(
          '#settingsList .settings-sync-email'
        ) as HTMLInputElement | null;
        el?.focus();
      });
    },
  });
}

function buildEnableForm(helpers: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-form';
  const desc = document.createElement('p');
  desc.className = 'settings-helptext';
  desc.textContent = 'Sync mirrors your notes through your account.';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn--primary';
  btn.textContent = 'Enable sync';
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    try {
      if (helpers?.enable) {
        await helpers.enable();
      } else {
        window.Platform?.optInSync?.(); // runtime — Platform has no ES export
        localStorage.setItem('sync_enabled', 'true');
        window.location.reload();
      }
    } catch (e: any) {
      console.warn('[settings] enable sync failed:', e);
      btn.disabled = false;
    }
  });
  wrap.append(desc, btn);
  return wrap;
}

// The signed-in header value: the account email as a dropdown button (caret via
// `.settings-value-btn-menu`, like the Workspaces switcher). Clicking opens a
// small menu whose only item is "Sign out".
function buildSignedInValue(ctx: SettingsSectionCtx, helpers: any): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'settings-value-btn settings-value-btn-menu';
  btn.textContent = helpers.userEmail || 'Signed in';
  btn.title = helpers.userEmail || 'Account';
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-label', `Signed in as ${helpers.userEmail || ''}; account menu`);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleMenu(btn, () =>
      openMenu(btn, [
        { label: 'Sign out', danger: true, onSelect: () => _doSignOut(ctx, helpers) },
        { label: 'Delete account…', danger: true, onSelect: () => _doDeleteAccount() },
      ])
    );
  });
  return btn;
}

async function _doSignOut(ctx: SettingsSectionCtx, helpers: any): Promise<void> {
  // Sign-out wipes this device's local copy of the notes (they re-sync on the
  // next sign-in; the encryption key stays saved, so nothing else is needed).
  // Say so up front since the notes visibly disappear until you sign back in.
  const ok = await confirm({
    title: 'Sign out?',
    message:
      'Your notes are removed from this device and re-sync when you sign back in. ' +
      'Your encryption key stays saved here, so nothing extra is needed.',
    confirmLabel: 'Sign out',
  });
  if (!ok) return;
  try {
    await helpers.disable();
    _syncStep = 'idle';
    _syncPendingEmail = '';
  } catch (e: any) {
    console.warn('[settings] sign out failed:', e);
  }
  ctx.invalidate();
}

// Permanently delete the whole account (server-side cascade). Behind a typed
// (DELETE) confirm. The edge function refuses if the user solely-owns a shared
// workspace; that message is surfaced via toast.
async function _doDeleteAccount(): Promise<void> {
  const typed = await prompt({
    title: 'Delete account — permanent',
    message:
      'This permanently deletes your account and ALL its notes everywhere — there ' +
      'is no undo. Type DELETE to confirm.',
    confirmLabel: 'Delete account',
    placeholder: 'DELETE',
  });
  if (typed == null) return;
  if (typed.trim() !== 'DELETE') {
    toast('Deletion cancelled — confirmation text did not match.', 'warning');
    return;
  }
  try {
    await deleteAccount(); // reloads signed-out on success
  } catch (e: any) {
    toast(e?.message || 'Account deletion failed.', 'warning');
  }
}

// Builds the current step's form (idle or sent) with an always-present error div
// (hidden when empty) so `updateAuthForm` can toggle it without a remount.
function buildAuthForm(ctx: SettingsSectionCtx, helpers: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-form';

  if (_syncStep === 'idle') {
    const intro = document.createElement('p');
    intro.className = 'settings-helptext';
    intro.textContent = "We'll email you a sign-in code.";
    const email = document.createElement('input');
    email.type = 'email';
    email.placeholder = 'you@example.com';
    // `settings-sync-email` lets the header's "Sign in" value focus this field.
    email.className = 'settings-mini-input settings-sync-email';
    email.value = _syncPendingEmail;
    email.required = true;
    const send = document.createElement('button');
    send.type = 'button';
    send.className = 'btn btn--primary';
    send.textContent = _syncBusy ? 'Sending…' : 'Send code';
    send.disabled = _syncBusy;
    const submit = async () => {
      const val = (email.value || '').trim();
      if (!val) return;
      _syncPendingEmail = val; // survive a failed send
      _syncBusy = true;
      _syncError = '';
      ctx.invalidate();
      try {
        await helpers.sendMagicLink(val);
        _syncStep = 'sent';
      } catch (e: any) {
        _syncError = e?.message || 'Could not send code.';
      }
      _syncBusy = false;
      ctx.invalidate();
    };
    send.addEventListener('click', submit);
    email.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });
    wrap.append(intro, email, send);
  } else if (_syncStep === 'sent') {
    const intro = document.createElement('p');
    intro.className = 'settings-helptext';
    intro.textContent = `Code sent to ${_syncPendingEmail}.`;
    const code = document.createElement('input');
    code.type = 'text';
    code.inputMode = 'numeric';
    code.autocomplete = 'one-time-code';
    code.maxLength = 6;
    code.placeholder = '123456';
    code.className = 'settings-mini-input';
    const verify = document.createElement('button');
    verify.type = 'button';
    verify.className = 'btn btn--primary';
    verify.textContent = _syncBusy ? 'Verifying…' : 'Verify';
    verify.disabled = _syncBusy;
    const submit = async () => {
      const val = (code.value || '').trim();
      if (!val) return;
      _syncBusy = true;
      _syncError = '';
      ctx.invalidate();
      try {
        await helpers.verifyOtp(_syncPendingEmail, val);
        _syncStep = 'authed';
      } catch (e: any) {
        _syncError = e?.message || 'Invalid code.';
      }
      _syncBusy = false;
      ctx.invalidate();
    };
    verify.addEventListener('click', submit);
    code.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });
    const resend = document.createElement('button');
    resend.type = 'button';
    resend.className = 'btn';
    resend.textContent = 'Resend';
    resend.addEventListener('click', async () => {
      try {
        await helpers.sendMagicLink(_syncPendingEmail);
        toast('Code resent.', 'info');
      } catch (e: any) {
        _syncError = e?.message || 'Resend failed.';
        ctx.invalidate();
      }
    });
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn';
    cancel.textContent = 'Use different email';
    cancel.addEventListener('click', () => {
      _syncStep = 'idle';
      _syncError = '';
      ctx.invalidate();
    });
    const subRow = document.createElement('div');
    subRow.className = 'settings-form-aux';
    subRow.append(resend, cancel);
    wrap.append(intro, code, verify, subRow);
  }

  wrap.appendChild(errorDiv(_syncError));
  return wrap;
}

// Patch the retained two-step form's button + error from current state.
function updateAuthForm(el: HTMLElement): void {
  const btn = el.querySelector('.btn.btn--primary') as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = _syncBusy;
    btn.textContent =
      _syncStep === 'sent'
        ? _syncBusy
          ? 'Verifying…'
          : 'Verify'
        : _syncBusy
          ? 'Sending…'
          : 'Send code';
  }
  setErrorDiv(el.querySelector('.settings-error'), _syncError);
}

// Called by the host's auth-state listener: if we were waiting on a code and
// auth completed out of band, advance to 'authed' so the next render shows
// "Signed in as …" without a manual refresh.
/** @returns {void} */
export function handleAuthStateChange() {
  if (_syncStep === 'sent' && getSyncHelpers()?.authenticated) _syncStep = 'authed';
}
